import { useEffect, useState } from 'react';
import { getSubscription, cancelSubscription } from '@genfun/core/api/auth';
import { LegalLayout } from './Legal';
import './Legal.css';

const SUPPORT_EMAIL = 'support@pornoclaw.com';

// The actionable part of rule 3: a quiet inline link that lets an active
// subscriber cancel right from the payment rules page (two-step confirm).
// Unlike MakeNude, this app has no app-level subStatus, so the box fetches
// the subscription itself.
function CancelSubscriptionLine({ token, refreshUser }) {
    const [subStatus, setSubStatus] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [confirm, setConfirm] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        let alive = true;
        if (!token) { setSubStatus(null); setLoaded(true); return undefined; }
        (async () => {
            try {
                const [sub, status] = await getSubscription(token);
                if (alive) setSubStatus(status === 200 && sub?.status ? sub.status : null);
            } catch (e) {
                if (alive) setSubStatus(null);
            } finally {
                if (alive) setLoaded(true);
            }
        })();
        return () => { alive = false; };
    }, [token]);

    const handleCancel = async () => {
        if (!confirm) { setConfirm(true); return; }
        setCancelling(true);
        setError(null);
        try {
            const [data, code] = await cancelSubscription(token);
            if (code !== 200) {
                setError((data && data.detail) || 'Failed to cancel');
                return;
            }
            setDone(true);
            setConfirm(false);
            refreshUser?.();
        } catch (e) {
            setError(e.message || String(e));
        } finally {
            setCancelling(false);
        }
    };

    if (!token) {
        return <p>To cancel a subscription, log in to your account first.</p>;
    }
    if (!loaded) {
        return null;
    }
    if (done || subStatus === 'cancelled') {
        return (
            <p>
                Your subscription is cancelled. You keep access and your remaining credits until the end
                of the paid period, and you will not be charged again.
            </p>
        );
    }
    if (!subStatus) {
        return <p>You don&rsquo;t have an active subscription.</p>;
    }
    return (
        <p>
            To stop your subscription, you can{' '}
            <button className="legalCancelLink" onClick={handleCancel} disabled={cancelling}>
                {cancelling
                    ? 'cancelling…'
                    : confirm
                        ? 'click again to confirm the cancellation'
                        : 'cancel it here'}
            </button>
            {confirm && !cancelling && (
                <>
                    {' '}·{' '}
                    <button className="legalCancelLink" onClick={() => setConfirm(false)}>
                        keep it
                    </button>
                </>
            )}
            {error && <span className="legalCancelError"> {error}</span>}
        </p>
    );
}

export function PaymentRules({ onNavigate, token, refreshUser }) {
    return (
        <LegalLayout title="Payment Rules" eyebrow="Billing" onNavigate={onNavigate} lastUpdated="29 July 2026">
            <section>
                <p className="legalIntro">
                    These rules describe how payments work on Pornoclaw: what you are charged, how renewals
                    happen, and how to cancel or get help with a payment.
                </p>
            </section>

            <section>
                <h2>1. Plans and pricing</h2>
                <p>
                    We offer two subscription plans: Starter at $9.99 per week (50 credits) and Premium at
                    $19.99 per week (400 credits). Credits are added to your balance with every successful
                    payment and are spent on photo and video generations. Prices are shown at checkout before
                    you pay.
                </p>
            </section>

            <section>
                <h2>2. Recurring billing</h2>
                <p>
                    Subscriptions renew automatically. Your payment method is charged the plan price every
                    week on the day corresponding to your original purchase date, until you cancel. All
                    payments are processed securely by our payment provider — we never see or store your full
                    card details.
                </p>
            </section>

            <section>
                <h2>3. Cancelling your subscription</h2>
                <p>
                    You can cancel at any time — no calls, no questions. After cancelling, your plan stays
                    active until the end of the period you already paid for, and no further charges are made.
                </p>
                <CancelSubscriptionLine token={token} refreshUser={refreshUser} />
            </section>

            <section>
                <h2>4. Refunds</h2>
                <p>
                    Refunds are handled according to our <button className="legalInlineLink" onClick={() => onNavigate('terms')}>Terms of Service</button>.
                    If you believe you were charged in error, contact us and we will review your case.
                </p>
            </section>

            <section>
                <h2>5. Payment support</h2>
                <p>
                    For any billing question — an unrecognized charge, a failed payment, or help with
                    cancellation — email us at <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>. We
                    respond within a reasonable timeframe, typically within 7 business days.
                </p>
            </section>
        </LegalLayout>
    );
}
